import { useState } from "react";
import { Calendar, Phone, Mail, User, MessageSquare, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const BookingSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    concern: "",
    message: "",
    // Honeypot fields
    firstName: "",
    website: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const concerns = [
    "Diastasis Recti / Tummy Bulge",
    "Post Pregnancy Back Pain",
    "Weak Core Strength",
    "Urinary Incontinence",
    "General Consultation",
    "Other",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedName = formData.name.trim();
    const trimmedPhone = formData.phone.trim();

    // Honeypot check: If these are filled, it's a bot
    if (formData.firstName || formData.website) {
      console.warn("Spam submission detected");
      // Simulate success to the bot
      setIsSuccess(true);
      return;
    }

    if (!trimmedName || !trimmedPhone) {
      toast.error("Please fill in your name and phone number");
      return;
    }

    if (trimmedPhone.length < 10) {
      toast.error("Please enter a valid phone number (at least 10 digits)");
      return;
    }

    setIsSubmitting(true);

    try {
      // Clean data: use trimmed values and convert empty strings to null for the API
      const submissionData = {
        name: trimmedName,
        phone: trimmedPhone,
        email: formData.email.trim() || null,
        concern: formData.concern || null,
        message: formData.message.trim() || null,
        firstName: formData.firstName,
        website: formData.website,
      };

      // Send to backend (it forwards data to webhook)
      await axios.post(`${API}/bookings`, submissionData);

      setIsSuccess(true);
      toast.success("Booking request submitted successfully! We will contact you shortly.");

      setFormData({
        name: "",
        phone: "",
        email: "",
        concern: "",
        message: "",
        firstName: "",
        website: "",
      });
    } catch (error) {
      console.error("Booking error:", error);
      toast.error("Failed to submit request. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking" className="section-spacing bg-gradient-to-b from-blue-900 to-blue-950" data-testid="booking-section">
      <div className="section-container">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="text-white">
            <span className="inline-block bg-white/10 text-pink-200 px-4 py-2 rounded-full text-sm font-medium mb-6">
              Take The First Step
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white" data-testid="booking-headline">
              Don't Ignore Your Core Health
            </h2>
            <p className="text-blue-200 text-lg mb-8">
              Book your consultation today and begin your recovery journey. Dr. Seema will assess your condition and create a personalized treatment plan.
            </p>

            {/* Contact Options */}
            <div className="space-y-4 mb-8">
              <a
                href="tel:+918800095625"
                className="flex items-center gap-4 bg-white/10 hover:bg-white/20 rounded-xl p-4 transition-colors"
                data-testid="booking-call-btn"
              >
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Phone className="text-white" size={24} />
                </div>
                <div>
                  <p className="font-semibold">Call Now</p>
                  <p className="text-blue-200">+91 88000 95625</p>
                </div>
              </a>

              <a
                href="https://wa.me/918800095625?text=Hi%2C%20I%20want%20to%20book%20a%20consultation%20for%20Diastasis%20Recti%20Treatment"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-[#25D366]/20 hover:bg-[#25D366]/30 rounded-xl p-4 transition-colors"
                data-testid="booking-whatsapp-btn"
              >
                <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="white" className="w-6 h-6">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold">WhatsApp Consultation</p>
                  <p className="text-blue-200">Quick response within minutes</p>
                </div>
              </a>
            </div>

            {/* Urgency */}
            <div className="flex items-center gap-2 text-pink-200" data-testid="booking-urgency">
              <span className="w-2 h-2 bg-pink-400 rounded-full animate-pulse"></span>
              Limited slots available - Book now to secure your appointment
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-3xl p-8 shadow-2xl" data-testid="booking-form-container">
            {isSuccess ? (
              <div className="text-center py-8 success-animation" data-testid="booking-success">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="text-emerald-600" size={48} />
                </div>
                <h3 className="text-2xl font-bold text-blue-900 mb-2">Booking Received!</h3>
                <p className="text-stone-600 mb-6">
                  Thank you for your interest. Our team will contact you within 24 hours to confirm your appointment.
                </p>
                <Button
                  onClick={() => setIsSuccess(false)}
                  className="btn-primary"
                  data-testid="book-another-btn"
                >
                  Book Another Consultation
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="booking-form">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Calendar className="text-blue-700" size={20} />
                  </div>
                  <h3 className="text-xl font-bold text-blue-900">Book Your Consultation</h3>
                </div>

                {/* Honeypot Fields (Hidden from users) */}
                <div className="sr-only" aria-hidden="true">
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div className="space-y-4">
                  {/* Name */}
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
                    <Input
                      type="text"
                      name="name"
                      placeholder="Your Name *"
                      value={formData.name}
                      onChange={handleChange}
                      className="input-field pl-12"
                      required
                      data-testid="booking-name-input"
                    />
                  </div>

                  {/* Phone */}
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number *"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input-field pl-12"
                      required
                      data-testid="booking-phone-input"
                    />
                  </div>

                  {/* Email */}
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
                    <Input
                      type="email"
                      name="email"
                      placeholder="Email (Optional)"
                      value={formData.email}
                      onChange={handleChange}
                      className="input-field pl-12"
                      data-testid="booking-email-input"
                    />
                  </div>

                  {/* Concern */}
                  <Select
                    value={formData.concern}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, concern: value }))}
                  >
                    <SelectTrigger className="input-field" data-testid="booking-concern-select">
                      <SelectValue placeholder="Select Your Concern" />
                    </SelectTrigger>
                    <SelectContent>
                      {concerns.map((concern, index) => (
                        <SelectItem key={index} value={concern} data-testid={`concern-option-${index}`}>
                          {concern}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Message */}
                  <div className="relative">
                    <MessageSquare className="absolute left-4 top-4 text-stone-400" size={18} />
                    <Textarea
                      name="message"
                      placeholder="Tell us about your condition (Optional)"
                      value={formData.message}
                      onChange={handleChange}
                      className="input-field pl-12 min-h-[100px] resize-none"
                      data-testid="booking-message-input"
                    />
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    disabled={isSubmitting}
                    data-testid="booking-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="animate-spin" size={20} />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Calendar size={20} />
                        Request Consultation
                      </>
                    )}
                  </Button>
                </div>

                {/* Note */}
                <p className="text-xs text-stone-500 text-center mt-4" data-testid="booking-note">
                  By submitting, you agree to be contacted by our team regarding your consultation.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
